import { useCallback, useEffect, useRef, useState } from 'react';
import { BottomSheet } from '@/components/base/BottomSheet';

export interface CapturedMedia {
  blob: Blob;
  kind: 'image' | 'video';
}

interface MediaCaptureSheetProps {
  open: boolean;
  onClose: () => void;
  onCaptured: (media: CapturedMedia) => void;
  /** Stills only — hides the recorder and skips the microphone permission entirely.
   * A thửa-đất photo has no use for a 60-second clip or for audio. */
  photoOnly?: boolean;
}

const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;
/** Hard stop so a forgotten recording cannot grow until the upload fails. */
const MAX_VIDEO_MS = 60_000;
/**
 * Left to itself a 1080p MediaRecorder picks a bitrate that puts a full 60-second
 * clip near — and sometimes past — the server's 25 MB body limit, where the
 * upload dies with a 413 the farmer can do nothing about. ~2 Mbps keeps a
 * maximum-length clip around 15 MB, which is also a kinder thing to push over
 * a field 4G connection, and is plenty for a field note.
 */
const VIDEO_BITS_PER_SECOND = 2_000_000;
const AUDIO_BITS_PER_SECOND = 64_000;

/** First supported container — Safari has no webm, Chrome/Firefox have no mp4 here. */
function pickVideoMime(): string | undefined {
  const candidates = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t));
}

/**
 * In-app camera for farm logs: a shutter for stills and a recorder for video.
 *
 * The form previously offered only `<input type="file" capture="environment">`.
 * `capture` is a hint a mobile browser may honour by opening the camera app, and
 * on desktop it does nothing at all — so there was no way to actually take a photo
 * or record a clip from inside the app, which is what a farmer standing in a field
 * needs. The gallery picker stays; this sits beside it.
 */
export function MediaCaptureSheet({ open, onClose, onCaptured, photoOnly = false }: MediaCaptureSheetProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const stopTimerRef = useRef<number | undefined>(undefined);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);

  const stopStream = useCallback(() => {
    window.clearTimeout(stopTimerRef.current);
    recorderRef.current?.state === 'recording' && recorderRef.current.stop();
    recorderRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setReady(false);
    setRecording(false);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    // getUserMedia is refused outright in a non-secure context (plain HTTP on
    // anything but localhost) — every modern mobile browser blocks it with no
    // permission prompt at all, which otherwise looks identical to "the device
    // has no camera". Naming this specifically (Feedback 05, mục 10 — "lỗi gọi
    // thiết bị") points at the real fix (serve the app over HTTPS) instead of a
    // dead end of retrying on the same device.
    if (typeof window.isSecureContext === 'boolean' && !window.isSecureContext) {
      setError('Trang đang tải qua kết nối không bảo mật (HTTP) nên trình duyệt chặn camera/micro. Cần truy cập qua HTTPS để quay video — hãy báo quản trị viên.');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Trình duyệt không hỗ trợ camera. Hãy dùng "Chọn từ thư viện".');
      return;
    }
    try {
      // Audio is requested up front: a MediaRecorder can only capture the tracks
      // the stream already has, so asking later would mean a silent clip.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: !photoOnly,
      });
      streamRef.current = stream;
      // The OS/another app can yank the camera away mid-session (backgrounding,
      // a phone call, another tab grabbing it) — without this the preview just
      // freezes with no explanation and "Dừng & lưu" produces an empty clip.
      stream.getTracks().forEach((t) => {
        t.onended = () => setError('Camera/micro đã bị ngắt (có thể do ứng dụng khác dùng, hoặc mất kết nối thiết bị). Hãy mở lại và thử quay lại.');
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setReady(true);
    } catch (err) {
      const name = err instanceof DOMException ? err.name : '';
      setError(
        name === 'NotAllowedError'
          ? `Bạn đã từ chối quyền camera${photoOnly ? '' : '/micro'}. Hãy cho phép trong cài đặt trình duyệt, hoặc dùng "Chọn từ thư viện".`
          : name === 'NotFoundError'
            ? 'Không tìm thấy camera trên thiết bị. Hãy dùng "Chọn từ thư viện".'
            : name === 'NotReadableError' || name === 'TrackStartError'
              ? 'Camera đang được ứng dụng khác sử dụng. Hãy đóng ứng dụng camera/gọi video khác rồi thử lại.'
              : name === 'OverconstrainedError'
                ? 'Camera thiết bị không đáp ứng được yêu cầu quay (độ phân giải). Hãy thử một thiết bị khác.'
                : name === 'SecurityError'
                  ? 'Trình duyệt chặn camera vì kết nối không an toàn (không phải HTTPS). Hãy báo quản trị viên.'
                  : `Không mở được camera${name ? ` (${name})` : ''}. Hãy dùng "Chọn từ thư viện".`,
      );
    }
  }, [photoOnly]);

  useEffect(() => {
    if (open) void start();
    else stopStream();
    return stopStream;
  }, [open, start, stopStream]);

  useEffect(() => {
    if (!recording) return;
    const id = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [recording]);

  const takePhoto = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return;
    setBusy(true);
    try {
      const scale = Math.min(1, MAX_EDGE / Math.max(video.videoWidth, video.videoHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', JPEG_QUALITY));
      if (!blob) throw new Error('encode failed');
      onCaptured({ blob, kind: 'image' });
      onClose();
    } catch {
      setError('Không chụp được ảnh. Hãy thử lại.');
    } finally {
      setBusy(false);
    }
  };

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;
    const mimeType = pickVideoMime();
    if (!mimeType) {
      setError('Trình duyệt không hỗ trợ quay video. Hãy dùng "Chọn từ thư viện".');
      return;
    }
    chunksRef.current = [];
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
    } catch (err) {
      setError(`Không khởi tạo được bộ ghi video (${err instanceof Error ? err.message : 'lỗi không rõ'}). Hãy dùng "Chọn từ thư viện".`);
      return;
    }
    recorder.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
    recorder.onerror = (e) => {
      // MediaRecorder fires this on real encoding failures (out of storage/memory,
      // track ended mid-record) — previously silent, so a failed recording just
      // looked like nothing happened when the sheet closed with no file produced.
      const err = (e as { error?: DOMException }).error;
      setError(`Quay video thất bại${err ? ` (${err.name})` : ''}. Hãy thử lại hoặc dùng "Chọn từ thư viện".`);
      setRecording(false);
      chunksRef.current = [];
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      chunksRef.current = [];
      if (blob.size > 0) {
        onCaptured({ blob, kind: 'video' });
        onClose();
      } else {
        setError('Video không có dữ liệu (có thể camera bị ngắt giữa chừng). Hãy thử quay lại.');
      }
    };
    recorder.start();
    recorderRef.current = recorder;
    setRecording(true);
    setElapsed(0);
    stopTimerRef.current = window.setTimeout(() => stopRecording(), MAX_VIDEO_MS);
  };

  const stopRecording = () => {
    window.clearTimeout(stopTimerRef.current);
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    setRecording(false);
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={photoOnly ? 'Chụp ảnh' : 'Chụp ảnh / Quay video'}>
      <div className="space-y-3">
        <div className="relative w-full aspect-[3/4] sm:aspect-video overflow-hidden rounded-xl bg-gray-900">
          <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${ready ? '' : 'invisible'}`} />
          {!ready && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4 text-center">
              {error ? (
                <>
                  <span className="w-10 h-10 flex items-center justify-center"><i className="ri-camera-off-line text-gray-400 text-2xl" /></span>
                  <p className="text-xs text-gray-300" role="alert">{error}</p>
                </>
              ) : (
                <>
                  <span className="w-10 h-10 flex items-center justify-center"><i className="ri-camera-line text-gray-400 text-2xl animate-pulse" /></span>
                  <span className="text-xs text-gray-400">Đang mở camera...</span>
                </>
              )}
            </div>
          )}
          {recording && (
            <div className="absolute top-2 left-2 flex items-center gap-1.5 rounded-full bg-red-600/90 px-2 py-1 text-[11px] font-semibold text-white">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              {String(Math.floor(elapsed / 60)).padStart(2, '0')}:{String(elapsed % 60).padStart(2, '0')}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={takePhoto}
            disabled={!ready || busy || recording}
            data-testid="capture-photo"
            className="flex-1 h-12 rounded-xl bg-agrida-600 text-white text-sm font-semibold disabled:bg-gray-300 flex items-center justify-center gap-2"
          >
            <i className="ri-camera-line" />
            {busy ? 'Đang xử lý...' : 'Chụp ảnh'}
          </button>
          {!photoOnly && (
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            disabled={!ready || busy}
            data-testid="capture-video"
            className={`flex-1 h-12 rounded-xl text-sm font-semibold disabled:bg-gray-300 disabled:text-white flex items-center justify-center gap-2 ${
              recording ? 'bg-red-600 text-white' : 'border border-slate-200 text-slate-700'
            }`}
          >
            <i className={recording ? 'ri-stop-circle-line' : 'ri-record-circle-line'} />
            {recording ? 'Dừng & lưu' : 'Quay video'}
          </button>
          )}
        </div>
        <p className="text-[11px] text-slate-400 text-center">
          {photoOnly ? 'Ảnh được tải lên ngay sau khi chụp.' : 'Video tối đa 60 giây. Ảnh và video được tải lên ngay sau khi chụp.'}
        </p>
      </div>
    </BottomSheet>
  );
}
